import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { subscriptionService, AssinaturaResponseDto } from './service/subscription.service';
import PaymentModal from '../../../components/PaymentModal/PaymentModal';

const ManageSubscriptionPage: React.FC = () => {
  const [subscription, setSubscription] = useState<AssinaturaResponseDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [cancelError, setCancelError] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchSubscription();
  }, []);

  const fetchSubscription = async () => {
    try {
      setLoading(true);
      const data = await subscriptionService.buscarAssinatura();
      if (!data) {
        navigate('/assinatura'); // Redirect to plans if no subscription found
        return;
      }
      setSubscription(data);
    } catch (error) {
      console.error('Erro ao buscar assinatura:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelSubscription = async () => {
    if (!subscription) return;

    try {
      setIsCancelling(true);
      setCancelError(null);
      await subscriptionService.deletarAssinatura();
      setIsCancelModalOpen(false);
      navigate('/assinatura');
    } catch (error) {
      setCancelError('Erro ao cancelar assinatura. Tente novamente mais tarde.');
    } finally {
      setIsCancelling(false);
    }
  };

  const handleUpdateCard = () => {
    setIsPaymentModalOpen(true);
  };

  const handleUpgrade = () => {
    navigate('/assinatura?upgrade=true');
  };

  const nextChargeDate = subscription?.dataProximaCobranca || subscription?.proximoVencimento;
  const cardLastDigits =
    subscription?.cartaoDeCredito?.finalCartao ||
    subscription?.cartaoDeCredito?.ultimo4Digitos ||
    subscription?.cartaoDeCredito?.numeroFinal ||
    subscription?.numeroCartao?.slice(-4);
  const cardBrand = subscription?.cartaoDeCredito?.bandeira;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-700"></div>
      </div>
    );
  }

  if (!subscription) return null;

  return (
    <div className="max-w-4xl mx-auto px-4 py-8 md:px-6 md:py-12">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Gerenciar Assinatura</h1>
        <p className="text-gray-600">Visualize os detalhes da sua assinatura e gerencie seus pagamentos.</p>
      </div>

      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden mb-8">
        <div className="p-6 md:p-8 border-b border-gray-100">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <p className="text-sm font-semibold text-blue-700 uppercase tracking-wider mb-1">Plano Atual</p>
              <h2 className="text-2xl font-bold text-gray-900">{subscription.plano || 'Plano Premium'}</h2>
            </div>
            <div className="bg-green-100 text-green-800 px-4 py-1.5 rounded-full text-sm font-medium self-start md:self-center">
              Ativo
            </div>
          </div>
        </div>

        <div className="p-6 md:p-8 grid grid-cols-1 md:grid-cols-2 gap-8">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Detalhes da Cobrança</h3>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-500">Valor:</span>
                <span className="font-medium text-gray-900">R$ {subscription.valor?.toFixed(2).replace('.', ',') || '0,00'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Próximo vencimento:</span>
                <span className="font-medium text-gray-900">
                  {nextChargeDate ? new Date(nextChargeDate).toLocaleDateString('pt-BR') : '-'}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Cartão:</span>
                <span className="font-medium text-gray-900">
                  {cardLastDigits ? `**** **** ${cardLastDigits}` : 'Cartão cadastrado'}
                  {cardBrand ? ` (${cardBrand})` : ''}
                </span>
              </div>
            </div>
            <button
              onClick={handleUpdateCard}
              className="mt-6 text-sm font-semibold text-blue-700 hover:text-blue-800 flex items-center gap-2"
            >
              Trocar cartão de crédito
            </button>
          </div>

          <div className="flex flex-col justify-center gap-4">
            <button
              onClick={handleUpgrade}
              className="w-full py-3 px-6 bg-blue-700 text-white font-semibold rounded-lg hover:bg-blue-800 transition-colors shadow-sm"
            >
              Fazer Upgrade de Plano
            </button>
            <button
              onClick={() => {
                setCancelError(null);
                setIsCancelModalOpen(true);
              }}
              className="w-full py-3 px-6 bg-white text-red-600 font-semibold rounded-lg border border-red-200 hover:bg-red-50 transition-colors"
            >
              Cancelar Assinatura
            </button>
          </div>
        </div>
      </div>

      <div className="bg-blue-50 border border-blue-100 rounded-xl p-6">
        <h3 className="text-blue-900 font-semibold mb-2">Dúvidas sobre sua assinatura?</h3>
        <p className="text-blue-800 text-sm">
          Entre em contato com nosso suporte através do e-mail <a href="mailto:suporte@sinalinvest.com.br" className="underline font-medium">suporte@sinalinvest.com.br</a>
        </p>
      </div>

      <PaymentModal 
        isOpen={isPaymentModalOpen}
        onClose={() => {
          setIsPaymentModalOpen(false);
          fetchSubscription();
        }}
        planName={subscription.plano || ''}
        planPrice={subscription.valor?.toString() || ''}
        planPeriod="mês" // Defaulting to month or we could add it to DTO
        isUpdate={true}
      />

      {isCancelModalOpen && (
        <div
          className="fixed inset-0 z-[999] bg-black/60 flex items-center justify-center p-4"
          onClick={() => {
            if (!isCancelling) {
              setIsCancelModalOpen(false);
            }
          }}
        >
          <div
            className="w-full max-w-md rounded-2xl bg-white shadow-2xl border border-gray-200 overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-6 py-5 border-b border-gray-100">
              <h3 className="text-xl font-bold text-gray-900">Confirmar cancelamento</h3>
              <p className="text-sm text-gray-600 mt-2">
                Tem certeza que deseja cancelar sua assinatura? Voce perdera acesso aos recursos premium ao final do periodo atual.
              </p>
            </div>
            <div className="px-6 py-5">
              {cancelError && (
                <p className="text-sm text-red-600 mb-4">{cancelError}</p>
              )}
              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setIsCancelModalOpen(false)}
                  disabled={isCancelling}
                  className="px-4 py-2 rounded-lg border border-gray-200 text-sm font-semibold text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  Voltar
                </button>
                <button
                  type="button"
                  onClick={handleCancelSubscription}
                  disabled={isCancelling}
                  className="px-4 py-2 rounded-lg text-sm font-semibold text-white bg-red-600 hover:bg-red-700 transition-colors disabled:opacity-70 disabled:cursor-not-allowed"
                >
                  {isCancelling ? 'Cancelando...' : 'Confirmar cancelamento'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageSubscriptionPage;
